import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { FormControlLabel, Grid2, Radio, RadioGroup, styled } from '@mui/material';
import { AppTypography } from '../../AppTypography/AppTypography';
import { CivilInsurancePlanSummary } from './CivilInsurancePlanSummary';
import { CustomInsuranceInput } from './CustomInsuranceInput';
import { useInsuranceConfig } from '../../../Helpers/Hooks';
import { StyledDivider } from '../StyledComponents';
import { useInsuranceStepLogic } from '../InsuranceStep.logic';

export const StyledRadio = styled(Radio)(
	({ theme }) => `
	color: ${theme.palette.insurance.secondary};
	border-width: 1px;
	&.Mui-checked {
		color: ${theme.palette.insurance.secondary};
	}`
);

export enum ClInsuranceType {
	AgentInsurance = 1,
	CustomInsurance,
	NoInsurance,
}

export interface Option {
	label: string;
	value: number;
}

export const CivilInsurancePlanSelect = () => {
	const { t } = useTranslation('common');
	const { watch, unregister, control } = useFormContext();
	const { clInsuranceRequired } = useInsuranceConfig();
	const { uncheckBoxes } = useInsuranceStepLogic();
	const selectedType = watch('civilLiabilityInsuranceType');

	const radioOptions: Option[] = [
		{ label: t('insurance.agentInsurance'), value: ClInsuranceType.AgentInsurance },
		{ label: t('insurance.customInsurance'), value: ClInsuranceType.CustomInsurance },
		{ label: t('insurance.noInsurance'), value: ClInsuranceType.NoInsurance },
	];

	const getOption = (type: ClInsuranceType) => radioOptions.find((x) => x.value === type);

	const isSelected = (type: ClInsuranceType) => getOption(type)?.value.toString() === selectedType?.toString();

	const renderOption = (type: ClInsuranceType) => {
		const option = getOption(type);
		return option ? (
			<FormControlLabel
				labelPlacement='end'
				label={<AppTypography element={'body2'} textAlign={'left'} bold text={option.label} />}
				value={option.value.toString()}
				control={<StyledRadio checked={isSelected(type)} disableRipple />}
			/>
		) : undefined;
	};

	useEffect(() => {
		if (isSelected(ClInsuranceType.AgentInsurance)) {
			unregister('agencyInsurance.customInsurance');
			return;
		}

		if (isSelected(ClInsuranceType.CustomInsurance)) {
			unregister('agencyInsurance.civilLiabilityInsuredSum');
			return;
		}

		if (isSelected(ClInsuranceType.NoInsurance)) {
			unregister('agencyInsurance.civilLiabilityInsuredSum');
			unregister('agencyInsurance.customInsurance');
			return;
		}
	}, [selectedType]);

	return (
		<Controller
			name={'civilLiabilityInsuranceType'}
			control={control}
			defaultValue={ClInsuranceType.AgentInsurance}
			render={({ field }) => (
				<RadioGroup
					{...field}
					onChange={(_: any, value: string) => {
						field.onChange(value);
						uncheckBoxes();
					}}
					value={field.value}
				>
					<Grid2>
						{renderOption(ClInsuranceType.AgentInsurance)}
						{isSelected(ClInsuranceType.AgentInsurance) && (
							<Grid2 my={1}>
								<CivilInsurancePlanSummary />
							</Grid2>
						)}
					</Grid2>

					<StyledDivider my={1} />

					<Grid2>
						{renderOption(ClInsuranceType.CustomInsurance)}
						{isSelected(ClInsuranceType.CustomInsurance) && <CustomInsuranceInput />}
					</Grid2>

					{!clInsuranceRequired && <Grid2>{renderOption(ClInsuranceType.NoInsurance)}</Grid2>}
				</RadioGroup>
			)}
		/>
	);
};
